'use client';

// CryptoCard App Screens
// Using MUI (Material UI) + Tailwind-style classes via sx prop

import { FC, useState, SetStateAction } from 'react';

import Select from '@mui/material/Select';
import { Refresh } from '@mui/icons-material';
import MenuItem from '@mui/material/MenuItem';
import InfoIcon from '@mui/icons-material/Info';
import LinkIcon from '@mui/icons-material/Link';
import IconButton from '@mui/material/IconButton';
import ArrowBack from '@mui/icons-material/ArrowBack';
import FeedbackIcon from '@mui/icons-material/Feedback';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import {
  Tab,
  Box,
  Card,
  Tabs,
  Grid,
  Stack,
  Paper,
  Button,
  Divider,
  Container,
  Typography,
} from '@mui/material';

import Image from '../../../../components/image';
import { useLocales } from '../../../../locales';
import Iconify from '../../../../components/iconify';
import Scrollbar from '../../../../components/scrollbar';
import { ComponentTypeProps } from '../../../../types/page-generator';

function Deposit() {
  const [currency, setCurrency] = useState('USDT');
  const [portType, setPortType] = useState('(TRC-20)');
  const { currentLang } = useLocales();

  const balances: Record<string, string> = {
    USDT: '0.00',
    EUR: '89.56',
    BTC: '0.0023',
    ETH: '0.0341',
  };

  const portOptions: Record<string, string[]> = {
    USDT: ['(TRC-20)', '(ERC-20)', '(BEP-20)'],
    EUR: ['(TRC-20)', '(ERC-20)', '(BEP-20)'],
    BTC: ['(TRC-20)', '(ERC-20)', '(BEP-20)'],
    ETH: ['(TRC-20)', '(ERC-20)', '(BEP-20)'],
  };

  const handleCurrencyChange = (e: any) => {
    const newCurrency = e.target.value;
    setCurrency(newCurrency);
    const ports = portOptions[newCurrency];
    setPortType(ports.length ? ports[0] : '');
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 3,
          flexDirection: currentLang.value === 'he' ? 'row-reverse' : 'row',
        }}
      >
        <IconButton color="inherit">
          <ArrowBack sx={{ color: 'black' }} />
        </IconButton>
        <Typography variant="h6" sx={{ color: 'black' }}>
          Deposit
        </Typography>
      </Box>

      <Stack sx={{ border: '1px dashed black', borderRadius: 4 }}>
        <Box
          sx={{
            width: '100.5%',
            mx: -0.25,
            mt: -0.25,
            borderRadius: 4,
            backgroundColor: '#F55D52',
            padding: 2,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            boxShadow: 6,
            justifyContent: 'center',
          }}
        >
          <Box mr={2}>
            <Image src="/assets/images/hand-card.png" width={40} height={40} alt="hand" />
          </Box>

          <Box>
            <Select
              value={currency}
              onChange={handleCurrencyChange}
              variant="standard"
              disableUnderline
              sx={{
                color: 'white',
                fontSize: 18,
                fontWeight: 600,
                mb: 0.5,
                '& .MuiSelect-icon': { color: 'white', right: '4px' },
              }}
            >
              {Object.keys(balances).map((cur) => (
                <MenuItem key={cur} value={cur}>
                  {cur}
                </MenuItem>
              ))}
            </Select>

            <Typography fontSize={12}>Crypto Currency</Typography>

            <Typography fontSize={20} fontWeight={700} mt={1}>
              {balances[currency]} {currency}
            </Typography>

            {portOptions[currency]?.length > 0 && (
              <Select
                value={portType}
                onChange={(e) => setPortType(e.target.value)}
                variant="standard"
                disableUnderline
                sx={{
                  color: 'white',
                  fontSize: 14,
                  '& .MuiSelect-icon': { color: 'white', right: '4px' },
                  mt: 0.5,
                }}
              >
                {portOptions[currency].map((port) => (
                  <MenuItem key={port} value={port}>
                    Balance {port}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>
        </Box>
        <Stack alignItems="center" my={2}>
          <Typography>4611XXXX XXXX 6155</Typography>
          <Typography>Card Number</Typography>
        </Stack>
      </Stack>
      <Box
        sx={{
          mt: 2,
          borderRadius: 4,
          color: 'white',
          mx: 'auto',
          fontFamily: 'Arial, sans-serif',
        }}
      >
        {/* Верхняя секция */}
        <Box
          sx={{
            backgroundColor: '#5D4A85',
            borderRadius: 3,
            p: 2.5,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography fontSize={26} fontWeight={500} color="#C1B3D2">
              Deposit Amount
            </Typography>
            <Stack display="flex" alignItems="flex-end" justifyContent="space-between">
              <Typography fontSize={32} fontWeight={600} color="#C1B3D2">
                0.00
              </Typography>
              <Typography fontSize={14} fontWeight={400} color="#C1B3D2">
                USDT
              </Typography>
            </Stack>
          </Stack>
        </Box>
        <Stack width={1} p={1} alignItems="center">
          <Typography fontSize={12} color="#C1B3D2">
            Maximum deposit amount is <strong>10,000.00 USDT</strong> /{' '}
            <strong>10,000.00 USD</strong>
            <br />
            Minimum deposit amount is <strong>52.00 USDT</strong> / <strong>52.00 USD</strong>
          </Typography>
        </Stack>
        {/* Нижняя секция */}
        <Box
          sx={{
            backgroundColor: '#2B1A3C',
            borderRadius: 3,
            p: 2,
          }}
        >
          {[
            { label: 'Fee', value: '0.00 USDT' },
            { label: 'Estimated Crypto Amount', value: '0.00 USDT' },
            { label: 'Total Receive Currency Amount', value: '0.00 USD' },
          ].map((item, index) => (
            <Box key={item.label} mb={index < 2 ? 1.5 : 0}>
              <Box display="flex" justifyContent="space-between">
                <Typography fontSize={14} color="#C1B3D2">
                  {item.label}
                </Typography>
                <Typography fontSize={14} fontWeight={500}>
                  {item.value}
                </Typography>
              </Box>
              {index < 2 && <Divider sx={{ borderColor: '#4D3A65', mt: 1 }} />}
            </Box>
          ))}
        </Box>

        {/* Подсказка */}
        <Stack width={1} p={1} alignItems="center">
          <Typography fontSize={12} color="#A296B0">
            Due to currency price fluctuations, there may be a small difference between the final
            deduction amount and the displayed amount
          </Typography>
        </Stack>
      </Box>
    </Container>
  );
}

function Notifications() {
  const { currentLang } = useLocales();

  const notifications = [
    {
      id: 1,
      icon: 'tdesign:notification',
      title: 'Two-Factor Authentication-2FA Update',
    },
    {
      id: 2,
      icon: 'tdesign:notification',
      title: 'Holiday Notice- Good Friday',
    },
    {
      id: 3,
      icon: 'tdesign:notification',
      title: 'Introduction of Monthly Subscription Fees',
    },
    {
      id: 4,
      icon: 'tdesign:notification',
      title: 'Introduction of Monthly Subscription Fee',
    },
    {
      id: 5,
      icon: 'tdesign:notification',
      title: 'PT Card Balance Transfer to Crypto Wallet for Harel Gobani',
    },
    {
      id: 6,
      icon: 'tdesign:notification',
      title: 'New Card Application Details',
    },
    {
      id: 7,
      icon: 'tdesign:notification',
      title: 'System Upgrade Notice',
    },
    {
      id: 8,
      icon: 'fluent:tv-usb-48-regular',
      title: 'Online Payment',
      subtitle: 'Your Withdraw of 36.80 USD has been successfully processed',
      date: 'Feb 27, 2025 at 07:17 AM',
    },
    {
      id: 9,
      icon: 'fluent:tv-usb-48-regular',
      title: 'Online Payment',
      subtitle: 'Your Withdraw of 2.07 USD has been successfully processed',
      date: 'Feb 27, 2025 at 07:17 AM',
    },
  ];

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 3,
          flexDirection: currentLang.value === 'he' ? 'row-reverse' : 'row',
        }}
      >
        <IconButton color="inherit">
          <ArrowBack sx={{ color: 'black' }} />
        </IconButton>
        <Typography variant="h6" sx={{ color: 'black' }}>
          Notifications
        </Typography>
      </Box>

      <Stack spacing={2}>
        {notifications.map((n) => (
          <Box
            key={n.id}
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'flex-start',
              bgcolor: '#40334A',
              borderRadius: 2,
              px: 2,
              py: 2,
              cursor: 'pointer',
            }}
          >
            <Box
              sx={{
                bgcolor: '#1F1F1F',
                borderRadius: '50%',
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mt: '4px',
              }}
            >
              <Iconify icon={n.icon} sx={{ color: 'white', fontSize: 20 }} />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Stack direction="row" width={1} justifyContent="space-between" alignItems="center">
                <Typography sx={{ color: 'white', fontWeight: 600, fontSize: 14 }}>
                  {n.title}
                </Typography>
                {n.date && (
                  <Typography sx={{ color: '#8D8D8D', fontSize: 12 }}>{n.date}</Typography>
                )}
              </Stack>
              {n.subtitle && (
                <Typography sx={{ color: '#B5B5B5', fontSize: 13, mt: 0.5 }}>
                  {n.subtitle}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Stack>
    </Container>
  );
}

function CardInformationScreen() {
  const { currentLang } = useLocales();
  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Box
        sx={{
          mt: 3,
          p: 2,
          background: '#202D36',
          color: 'white',
          borderRadius: '20px',
        }}
      >
        <Stack
          direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 3 }}
        >
          <Typography variant="h6" sx={{ color: 'white' }}>
            Card Info
          </Typography>
          <Iconify icon="material-symbols:close-rounded" />
        </Stack>
        <Stack
          direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography>Transaction Fee</Typography>
          <Typography>0.20</Typography>
        </Stack>
        <Divider
          sx={{
            borderStyle: 'dashed',
            borderColor: 'rgba(255,255,255,0.3)', // или другой цвет
            my: 1,
          }}
        />
        <Stack
          direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography>Card Currency</Typography>
          <Typography>$ USD</Typography>
        </Stack>
        <Divider
          sx={{
            borderStyle: 'dashed',
            borderColor: 'rgba(255,255,255,0.3)', // или другой цвет
            my: 1,
          }}
        />
        <Stack
          direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography>Card Type</Typography>
          <Typography>Virtual</Typography>
        </Stack>
        <Divider
          sx={{
            borderStyle: 'dashed',
            borderColor: 'rgba(255,255,255,0.3)', // или другой цвет
            my: 1,
          }}
        />
        <Stack
          direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography>Issuing Fee</Typography>
          <Typography>0</Typography>
        </Stack>
        <Divider
          sx={{
            borderStyle: 'dashed',
            borderColor: 'rgba(255,255,255,0.3)', // или другой цвет
            my: 1,
          }}
        />
        <Stack
          direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography>Consumption Method</Typography>
          <Typography>Debit</Typography>
        </Stack>
        <Divider
          sx={{
            borderStyle: 'dashed',
            borderColor: 'rgba(255,255,255,0.3)', // или другой цвет
            my: 1,
          }}
        />
        <Stack
          direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography>First Recharge Amount</Typography>
          <Typography>5.00 USD</Typography>
        </Stack>
        <Divider
          sx={{
            borderStyle: 'dashed',
            borderColor: 'rgba(255,255,255,0.3)', // или другой цвет
            my: 1,
          }}
        />
        <Stack
          direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography>Minimum Recharge Amount</Typography>
          <Typography>52.00 USD</Typography>
        </Stack>
        <Divider
          sx={{
            borderStyle: 'dashed',
            borderColor: 'rgba(255,255,255,0.3)', // или другой цвет
            my: 1,
          }}
        />
        <Stack
          direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography>Maximum Recharge Amount</Typography>
          <Typography>10,000.00 USD</Typography>
        </Stack>
        <Divider
          sx={{
            borderStyle: 'dashed',
            borderColor: 'rgba(255,255,255,0.3)', // или другой цвет
            my: 1,
          }}
        />
        <Stack
          direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography>Spending Limit</Typography>
          <Typography>Unlimited</Typography>
        </Stack>
        <Divider
          sx={{
            borderStyle: 'dashed',
            borderColor: 'rgba(255,255,255,0.3)', // или другой цвет
            my: 1,
          }}
        />
        <Stack
          direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography>Minimum Cancellation Fee</Typography>
          <Typography>$ 1.00</Typography>
        </Stack>
        <Divider
          sx={{
            borderStyle: 'dashed',
            borderColor: 'rgba(255,255,255,0.3)', // или другой цвет
            my: 1,
          }}
        />
        <Stack
          direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography>Maximum Cancellation Fee</Typography>
          <Typography>$ 0.00</Typography>
        </Stack>
        <Divider
          sx={{
            borderStyle: 'dashed',
            borderColor: 'rgba(255,255,255,0.3)', // или другой цвет
            my: 1,
          }}
        />
        <Stack
          direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography>Review Time</Typography>
          <Typography>Within 24 hours</Typography>
        </Stack>
        <Divider
          sx={{
            borderStyle: 'dashed',
            borderColor: 'rgba(255,255,255,0.3)', // или другой цвет
            my: 1,
          }}
        />
        <Stack
          direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography>Supported Platforms</Typography>
          <Typography>Apple Pay, Google Pay</Typography>
        </Stack>
      </Box>
    </Container>
  );
}

function CardServicePanelOverview() {
  const { currentLang } = useLocales();
  const services = [
    {
      title: 'Address',
      icon: <LocationOnIcon fontSize="large" sx={{ color: '#FFC107' }} />,
    },
    {
      title: 'Quick Link',
      icon: <LinkIcon fontSize="large" sx={{ color: '#FFC107' }} />,
    },
    {
      title: 'Feedback',
      icon: <FeedbackIcon fontSize="large" sx={{ color: '#FFC107' }} />,
    },
  ];

  return (
    <Box sx={{ textAlign: 'left', color: 'white' }}>
      <Typography
        variant="h6"
        sx={{
          mb: 2,
          color: 'black',
          textAlign: currentLang.value === 'he' ? 'end' : 'start',
        }}
      >
        Card Service
      </Typography>

      {/* Верхний ряд: 2 карточки */}
      <Grid container spacing={2} justifyContent="center">
        {services.map((service, index) => (
          <Grid item xs={6} sm={4} key={index} sx={{ cursor: 'pointer' }}>
            <Paper
              elevation={6}
              sx={{
                // background: '#2a1e38',
                backgroundImage: `url('/assets/images/button-font.png')`,
                borderRadius: 3,
                padding: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: 120,
                width: 180,
              }}
            >
              {service.icon}
              <Typography
                variant="body1"
                sx={{
                  mt: 1,
                  color: '#FFC107 ',
                  fontSize: '12px',
                  textAlign: 'center',
                }}
              >
                {service.title}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

function CardPanel() {
  const services = [
    {
      title: 'Deposit',
      icon: (
        <Iconify
          icon="mage:direction-up-right"
          fontSize="large"
          sx={{ color: '#FFC107', transform: 'rotate(-180deg)' }}
        />
      ),
    },
    {
      title: 'Withdraw',
      icon: <Iconify icon="mage:direction-up-right" fontSize="large" sx={{ color: '#FFC107' }} />,
    },
    {
      title: 'Cards',
      icon: <Iconify icon="solar:card-bold" fontSize="large" sx={{ color: '#FFC107' }} />,
    },
  ];

  return (
    <Box sx={{ textAlign: 'left', color: 'white', mt: 1 }}>
      {/* Верхний ряд: 2 карточки */}
      <Grid container spacing={2} justifyContent="center">
        {services.map((service, index) => (
          <Grid item xs={6} sm={4} key={index} sx={{ cursor: 'pointer' }}>
            <Paper
              elevation={6}
              sx={{
                // background: '#2a1e38',
                backgroundImage: `url('/assets/images/button-font.png')`,
                borderRadius: 3,
                padding: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: 120,
                width: 180,
              }}
            >
              {service.icon}
              <Typography
                variant="body1"
                sx={{
                  mt: 1,
                  color: '#FFC107 ',
                  fontSize: '12px',
                  textAlign: 'center',
                }}
              >
                {service.title}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

function CardServicePanel() {
  const { currentLang } = useLocales();
  const topServices = [
    {
      title: 'Record',
      icon: <ReceiptLongIcon fontSize="large" sx={{ color: '#FFC107 ' }} />,
    },
    {
      title: 'Card Info',
      icon: <InfoIcon fontSize="large" sx={{ color: '#FFC107 ' }} />,
    },
  ];

  const bottomService = {
    title: 'Freeze/ Unfreeze',
    icon: <AccountBalanceWalletIcon fontSize="large" sx={{ color: '#FFC107 ' }} />,
  };

  return (
    <Box sx={{ textAlign: 'left', color: 'white' }}>
      <Typography
        variant="h6"
        sx={{
          my: 2,
          color: 'black',
          textAlign: currentLang.value === 'he' ? 'end' : 'start',
        }}
      >
        Card Service
      </Typography>

      {/* Верхний ряд: 2 карточки */}
      <Grid container spacing={2} justifyContent="center">
        {topServices.map((service, index) => (
          <Grid item xs={6} sm={4} key={index} sx={{ cursor: 'pointer' }}>
            <Paper
              elevation={6}
              sx={{
                // background: '#2a1e38',
                backgroundImage: `url('/assets/images/button-font.png')`,
                borderRadius: 3,
                padding: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: 120,
                width: 180,
              }}
            >
              {service.icon}
              <Typography
                variant="body1"
                sx={{
                  mt: 1,
                  color: '#FFC107 ',
                  fontSize: '12px',
                  textAlign: 'center',
                }}
              >
                {service.title}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Нижняя карточка по центру */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: 2,
          cursor: 'pointer',
        }}
      >
        <Paper
          elevation={6}
          sx={{
            // background: '#2a1e38',
            backgroundImage: `url('/assets/images/button-font.png')`,
            borderRadius: 3,
            padding: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: 180,
            height: 120,
          }}
        >
          {bottomService.icon}
          <Typography
            variant="body1"
            sx={{
              mt: 1,
              color: '#FFC107 ',
              whiteSpace: 'pre-line',
              textAlign: 'center',
              fontSize: '12px',
            }}
          >
            {bottomService.title}
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
}

function CardView() {
  const { currentLang } = useLocales();
  const myCards = [
    {
      id: 1,
      image: '/assets/images/card-1.png',
      name: 'CO-BRANDED RP CARD',
      info: 'Apple and Google Pay Support',
      status: 'Approved',
    },
    {
      id: 2,
      image: '/assets/images/card-2.png',
      name: 'Co-Branded PT Card',
      info: 'Apple and Google Pay Support',
      status: 'Canceled',
    },
  ];
  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <CardServicePanelOverview />
      <Stack
        direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="h6" sx={{ my: 2, color: 'black' }}>
          Mine
        </Typography>
        <Stack
          direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
          alignItems="center"
          sx={{ cursor: 'pointer' }}
        >
          <Iconify icon="ph:arrow-up-bold" sx={{ color: 'red', transform: 'rotate(45deg)' }} />
          <Typography variant="h6" sx={{ my: 2, color: 'red' }}>
            All (2)
          </Typography>
        </Stack>
      </Stack>
      <Stack width={1} gap={2}>
        {myCards.map((card, id) => (
          <Stack
            key={id}
            direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
            width={1}
            justifyContent="space-between"
            alignItems="center"
            sx={{
              background: '#2b1d36',
              borderRadius: '20px',
              p: 2,
              py: 4,
              position: 'relative',
            }}
          >
            <Box
              sx={
                currentLang.value === 'he'
                  ? {
                      position: 'absolute',
                      top: 0,
                      left: '50px',
                      background: card.status === 'Approved' ? 'green' : 'red',
                      borderBottomLeftRadius: '10px',
                      borderBottomRightRadius: '10px',
                      p: 0.5,
                    }
                  : {
                      position: 'absolute',
                      top: 0,
                      right: '50px',
                      background: card.status === 'Approved' ? 'green' : 'red',
                      borderBottomLeftRadius: '10px',
                      borderBottomRightRadius: '10px',
                      p: 0.5,
                    }
              }
            >
              <Typography
                sx={{
                  color: 'white',
                  fontSize: '8px',
                }}
              >
                {card.status}
              </Typography>
            </Box>
            <Image src={card.image} />
            <Stack>
              <Typography sx={{ color: 'white' }}>{card.name}</Typography>
              <Typography sx={{ color: '#F5F5F5' }}>{card.info}</Typography>
            </Stack>
            <Iconify icon="oui:arrow-right" sx={{ color: 'white' }} />
          </Stack>
        ))}
      </Stack>
      <Typography
        variant="h6"
        sx={{
          my: 2,
          color: 'black',
          textAlign: currentLang.value === 'he' ? 'end' : 'start',
        }}
      >
        New card
      </Typography>
      <Stack alignItems="center" justifyContent="center">
        <Image src="/assets/images/no-data.png" />
      </Stack>
    </Container>
  );
}

function CardOverview() {
  const [currency, setCurrency] = useState('USD');
  const { currentLang } = useLocales();

  const balances: any = {
    USD: '97.02',
    EUR: '89.56',
    BTC: '0.0023',
    ETH: '0.0341',
  };

  const myCards = [
    {
      id: 1,
      image: '/assets/images/small-wallet.png',
      name: '0.93 USD',
      info: 'Crypto Wallet',
      status: 'Approved',
    },
    {
      id: 2,
      image: '/assets/images/small-card.png',
      name: '91.14 USD',
      info: 'Cards',
      status: 'Canceled',
    },
  ];
  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Typography
        sx={{
          textAlign: currentLang.value === 'he' ? 'end' : 'start',
        }}
      >
        Good Afternoon!
      </Typography>
      <Typography
        variant="h4"
        sx={{ mb: 10, textAlign: currentLang.value === 'he' ? 'end' : 'start' }}
      >
        Harel Gobani
      </Typography>
      <Box
        sx={{
          border: '1px dashed white',
          borderRadius: '20px',
          position: 'relative',
          pt: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 150,
          background: '#3F3456',
        }}
      >
        <Box
          sx={{
            width: '80%',
            borderRadius: 3,
            p: 3,
            color: 'white',
            background: '#F55D52',
            boxShadow: 6,
            mt: -25,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: -20,
              right: -20,
              width: 120,
              height: 120,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.15)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: -10,
              right: -10,
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.25)',
            }}
          />
          <Stack
            direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
            sx={{
              alignItems: 'center',
              mt: 2,
            }}
          >
            <Typography variant="h4" sx={{ color: 'white' }}>
              {balances[currency]}
            </Typography>
            <Select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              disableUnderline
              variant="standard"
              size="small"
              sx={{
                ml: 2,
                bgcolor: 'transparent',
                color: 'white',
                border: 'none',
                direction: currentLang.value === 'he' ? 'rtl' : 'ltr', // Меняем направление текста и иконки
                '& .MuiSelect-icon': {
                  color: 'white',
                  right: currentLang.value === 'he' ? 'unset' : '4px',
                  left: currentLang.value === 'he' ? '-15px' : 'unset', // Ставим иконку слева при иврите
                },
                '& .MuiInputBase-input': {
                  padding: '6px 24px 6px 6px',
                },
                '&.MuiInputBase-root': {
                  margin: '0px !important',
                },
                '& .MuiSelect-select': {
                  fontSize: '20px',
                  textAlign: currentLang.value === 'he' ? 'right' : 'left', // Чтобы текст красиво вставал
                },
              }}
            >
              {Object.keys(balances).map((code) => (
                <MenuItem value={code} key={code}>
                  {code}
                </MenuItem>
              ))}
            </Select>
          </Stack>

          <Typography
            sx={{
              mb: 2,
              textAlign: currentLang.value === 'he' ? 'end' : 'start',
            }}
          >
            Total Assets
          </Typography>
        </Box>
      </Box>
      <CardPanel />
      <Stack
        direction="row"
        alignItems={currentLang.value === 'he' ? 'flex-end' : 'flex-start'}
        justifyContent={currentLang.value === 'he' ? 'flex-end' : 'flex-start'}
      >
        <Typography variant="h6" sx={{ my: 2, color: 'black' }}>
          Assets
        </Typography>
      </Stack>
      <Stack width={1} gap={2} direction="row">
        {myCards.map((card, id) => (
          <Stack
            key={id}
            direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
            width={1}
            justifyContent="space-between"
            alignItems="center"
            sx={{
              background: '#2b1d36',
              borderRadius: '20px',
              p: 2,
              py: 4,
              position: 'relative',
              cursor: 'pointer',
            }}
          >
            <Stack gap={2} alignItems={currentLang.value === 'he' ? 'flex-end' : 'flex-start'}>
              <Image src={card.image} sx={{ width: '30px' }} />
              <Stack alignItems={currentLang.value === 'he' ? 'flex-end' : 'flex-start'}>
                <Typography sx={{ color: 'white', fontWeight: 1000 }}>{card.name}</Typography>
                <Typography sx={{ color: '#F5F5F5' }}>{card.info}</Typography>
              </Stack>
            </Stack>
            <Iconify icon="simple-line-icons:arrow-right" sx={{ color: 'white' }} />
          </Stack>
        ))}
      </Stack>
    </Container>
  );
}

function Exchange() {
  const { currentLang } = useLocales();
  const [currentTab, setCurrentTab] = useState(0);
  const [selected, setSelected] = useState('All');
  const handleTabChange = (event: any, newValue: SetStateAction<number>) => {
    setCurrentTab(newValue);
  };
  const tabs = ['All', 'Deposit', 'Consumption', 'Transfer', 'Rewards', 'Other'];
  const myCards = [
    {
      id: 1,
      image: 'mdi:file-document-add-outline',
      date: 'Apr 14, 2025 at 09:51 AM',
      SubDate: '',
      name: ' Subscription Fee',
      info: '',
      status: 'Pending',
      count: '50.00 USDT',
    },

    {
      id: 2,
      image: 'tdesign:discount',
      date: 'Mar 30, 2025 at 09:45 PM',
      subDate: 'Post date Mar 30, 2025 at 09:45 PM',
      name: 'CO-BRANDED RP CARD',
      info: 'Transaction Fee',
      status: 'Approved',
      count: '0.20 USDT',
    },

    {
      id: 3,
      image: 'fluent:tv-usb-48-regular',
      date: 'Mar 30, 2025 at 09:45 PM',
      subDate: 'Post date Mar 30, 2025 at 09:45 PM',
      name: 'eBay O*26-12873-91868 San Jose US',
      info: '',
      status: 'Canceled',
      count: '79.35 USDT',
    },

    {
      id: 4,
      image: 'mdi:file-document-add-outline',
      date: 'Mar 30, 2025 at 10:45 PM',
      subDate: 'Post date Mar 30, 2025 at 09:45 PM',
      name: 'CO-BRANDED RP CARD',
      info: 'Transaction Fee',
      status: 'Approved',
      count: '80.20 USDT',
    },
  ];
  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Stack
        direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
        alignItems="center"
        justifyContent="space-between"
        my={2}
      >
        <Typography
          sx={{
            my: 2,
            color: 'black',
            fontWeight: 1000,
            fontSize: '18px',
          }}
        >
          Exchanga Pay Records
        </Typography>
        <Stack
          direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
          alignItems="center"
          sx={{ cursor: 'pointer' }}
        >
          <Typography sx={{ my: 2, color: 'red', fontSize: '14px', fontWeight: 1000 }}>
            Download Bill
          </Typography>
        </Stack>
      </Stack>
      <Box
        sx={{
          border: '1px dashed white',
          borderRadius: '10px',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'black',
          px: 2,
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          centered
          TabIndicatorProps={{
            style: {
              backgroundColor: 'red',
              height: '4px',
              borderRadius: '2px',
            },
          }}
          sx={{
            '& .MuiTab-root': {
              color: 'white', // цвет текста для всех вкладок
              fontSize: '1.2rem',
              textTransform: 'none',
            },
            '& .Mui-selected': {
              fontWeight: 'bold', // жирный текст у активной вкладки
            },
          }}
        >
          <Tab label="Transaction History" />
          <Tab label="Application Records" />
        </Tabs>
      </Box>

      <Scrollbar
        sx={{
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          WebkitOverflowScrolling: 'touch',
          py: 1,
          my: 2,
          scrollbarWidth: 'none', // для Firefox
          '&::-webkit-scrollbar': {
            display: 'none', // для WebKit
          },
        }}
      >
        <Stack direction="row" spacing={1} sx={{ display: 'inline-flex' }}>
          {tabs.map((tab) => (
            <Button
              key={tab}
              onClick={() => setSelected(tab)}
              variant="contained"
              disableElevation
              sx={{
                borderRadius: '24px',
                px: 3,
                py: 1.5,
                minWidth: 'fit-content',
                backgroundColor: selected === tab ? '#FF5A5F' : '#2A2238',
                color: selected === tab ? 'white' : '#C4C4C4',
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                '&:hover': {
                  backgroundColor: selected === tab ? '#FF4A4F' : '#3A2F4F',
                },
              }}
            >
              {tab}
            </Button>
          ))}
        </Stack>
      </Scrollbar>

      <Stack width={1} gap={2}>
        {myCards.map((card, id) => (
          <Stack
            key={id}
            direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
            width={1}
            justifyContent="space-between"
            alignItems="center"
            sx={{
              background: '#2b1d36',
              borderRadius: '20px',
              p: 2,
              py: 4,
              position: 'relative',
            }}
          >
            <Box
              sx={
                currentLang.value === 'he'
                  ? {
                      position: 'absolute',
                      top: 0,
                      left: '50px',
                      background: card.status === 'Approved' ? 'green' : 'red',
                      borderBottomLeftRadius: '10px',
                      borderBottomRightRadius: '10px',
                      p: 0.5,
                    }
                  : {
                      position: 'absolute',
                      top: 0,
                      right: '50px',
                      background: card.status === 'Approved' ? 'green' : 'red',
                      borderBottomLeftRadius: '10px',
                      borderBottomRightRadius: '10px',
                      p: 0.5,
                    }
              }
            >
              <Typography
                sx={{
                  color: 'white',
                  top: 0,
                  right: '50px',
                  fontSize: '8px',
                }}
              >
                {card.status}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 48,
                height: 48,
                background: 'black',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Iconify icon={card.image} sx={{ color: 'white', fontSize: 24 }} />
            </Box>
            <Stack gap={1} flex={1} mx={5}>
              <Stack>
                <Typography sx={{ color: 'white', fontSize: '14px', fontWeight: 1000 }}>
                  {card.date}
                </Typography>
                <Typography sx={{ color: '#F5F5F5', fontSize: '12px' }}>{card.subDate}</Typography>
              </Stack>
              <Stack>
                <Typography sx={{ color: '#F5F5F5', fontSize: '14px', fontWeight: 1000 }}>
                  {card.name}
                </Typography>
                <Typography sx={{ color: '#F5F5F5', fontSize: '14px' }}>{card.info}</Typography>
              </Stack>
            </Stack>
            <Typography sx={{ color: 'white' }}>{card.count}</Typography>
          </Stack>
        ))}
      </Stack>
    </Container>
  );
}

function CryptoWallet() {
  const { currentLang } = useLocales();

  const myCards = [
    {
      id: 1,
      image: '/assets/images/tether.png',
      name: 'USDT(POLYGON)',
      info: '0.93',
      status: 'Approved',
    },
    {
      id: 2,
      image: '/assets/images/tether.png',
      name: 'USDT(TRC-20)',
      info: '0.00',
      status: 'Canceled',
    },
  ];
  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexDirection: currentLang.value === 'he' ? 'row-reverse' : 'row',
        }}
      >
        <Stack direction={currentLang.value === 'he' ? 'row-reverse' : 'row'} alignItems="center">
          <IconButton color="inherit">
            <ArrowBack />
          </IconButton>
          <Typography variant="h6">Crypto Wallet</Typography>
        </Stack>
        <IconButton color="inherit">
          <Refresh />
        </IconButton>
      </Box>
      <Stack width={1} gap={2}>
        {myCards.map((card, id) => (
          <Stack
            key={id}
            direction={currentLang.value === 'he' ? 'row-reverse' : 'row'}
            width={1}
            justifyContent="space-between"
            alignItems="center"
            sx={{
              background: '#2b1d36',
              borderRadius: '20px',
              p: 2,
              position: 'relative',
              cursor: 'pointer',
            }}
          >
            <Image src={card.image} />
            <Stack>
              <Typography sx={{ color: 'white' }}>{card.name}</Typography>
            </Stack>
            <Typography sx={{ color: '#F5F5F5' }}>{card.info}</Typography>
          </Stack>
        ))}
      </Stack>
    </Container>
  );
}

function CardDetailsView() {
  const [viewCvv, setViewCvv] = useState(false);
  const { currentLang } = useLocales();

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexDirection: currentLang.value === 'he' ? 'row-reverse' : 'row',
        }}
      >
        <Stack direction={currentLang.value === 'he' ? 'row-reverse' : 'row'} alignItems="center">
          <IconButton color="inherit">
            <ArrowBack />
          </IconButton>
          <Typography variant="h6">Card</Typography>
        </Stack>
        <IconButton color="inherit">
          <Refresh />
        </IconButton>
      </Box>

      {/* Пунктирный фон только за карточкой */}

      <Box
        sx={{
          p: 2,
          mb: 4,
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          mt: 10,
        }}
      >
        {/* SVG фон */}
        <svg
          width="550"
          height="240"
          viewBox="0 0 550 240"
          style={{ position: 'absolute', top: 0, left: 0 }}
        >
          <path
            d="
      M25,0
      Q0,0 0,25
      L0,205
      Q0,230 25,230
      L265,190
      Q275,188 285,190
      L525,230
      Q550,230 550,205
      L550,25
      Q550,0 525,0
      Z"
            fill="#3D315B"
          />
          <path
            d="
      M25,0
      Q0,0 0,25
      L0,205
      Q0,230 25,230
      L265,190
      Q275,188 285,190
      L525,230
      Q550,230 550,205
      L550,25
      Q550,0 525,0
      Z"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeDasharray="6,4"
          />
        </svg>

        {/* Контент поверх */}
        <Card
          sx={{
            width: '90%',
            backgroundImage: 'url(/assets/images/card-font.png)',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '100%',
            // background: 'linear-gradient(135deg, #0d47a1 0%, #42a5f5 100%)',
            borderRadius: 3,
            p: 3,
            color: 'white',
            boxShadow: 6,
            mt: -10, // отрицательный отступ, чтобы выглядывала сверху
          }}
        >
          <Typography variant="h6" textAlign="start">
            **** **** **** ****
          </Typography>
          <Typography variant="subtitle1" textAlign="start" sx={{ mt: 2 }}>
            Harel Gobani
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 8,
              width: '100%',
            }}
          >
            <Stack direction="row" width={1} justifyContent="space-between" alignItems="center">
              <Button
                sx={{
                  borderRadius: '15px',
                  bgcolor: 'green',
                  '&: hover': { bgcolor: 'green' },
                }}
              >
                Approved
              </Button>

              <Stack
                alignItems="center"
                onClick={() => setViewCvv(!viewCvv)}
                sx={{ cursor: 'pointer' }}
              >
                <Typography variant="body2">CVV </Typography>
                {viewCvv ? (
                  <Typography variant="body2">852</Typography>
                ) : (
                  <Typography variant="body2">***</Typography>
                )}
              </Stack>

              <Stack alignItems="center">
                <Typography variant="body2">EXPIRY </Typography>
                <Typography variant="body2">XX/XX </Typography>
              </Stack>
            </Stack>
          </Box>
        </Card>
      </Box>
      <Box sx={{ position: 'relative', width: '100%', height: 200 }}>
        {/* SVG фон */}
        <svg
          width="550"
          height="200"
          viewBox="0 0 550 200"
          style={{ position: 'absolute', top: 0, left: 0 }}
        >
          <path
            d="
      M35,70
      Q10,70 10,95
      L10,160
      Q10,185 35,190
      L515,190
      Q540,185 540,160
      L540,95
      Q540,70 515,70
      L275,15
      Z"
            fill="black"
          />
          <path
            d="
      M35,70
      Q10,70 10,95
      L10,160
      Q10,185 35,190
      L515,190
      Q540,185 540,160
      L540,95
      Q540,70 515,70
      L275,15
      Z"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeDasharray="6,4"
          />
        </svg>

        {/* Контент поверх */}
        <Box sx={{ position: 'relative', zIndex: 1, pt: 7 }}>
          <Grid
            container
            spacing={1}
            sx={{
              justifyContent: 'center',
              flexDirection: currentLang.value === 'he' ? 'row-reverse' : 'row',
            }}
          >
            <Grid
              item
              xs={4}
              sx={{
                display: 'flex',
                width: 1,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Image
                src="/assets/images/hand-wallet.png"
                sx={{
                  width: '50px',
                }}
              />
            </Grid>

            <Grid item xs={4}>
              <Stack>
                <Typography variant="h4" align="center" sx={{ color: '#FFC107 ' }}>
                  91.14
                </Typography>
                <Typography variant="body2" align="center" sx={{ color: 'white' }}>
                  Card Balance (USD)
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={4}>
              <Typography />
            </Grid>
          </Grid>
          <Stack alignItems="center">
            <Divider sx={{ width: '90%', my: 1 }} />
          </Stack>
          <Typography variant="subtitle2" align="center" sx={{ mt: 1, color: 'white' }}>
            Card Name: CryptoCard Virtual
          </Typography>
        </Box>
      </Box>
      <Stack alignItems="center">
        {currentLang.value === 'he' ? (
          <Button
            variant="contained"
            startIcon={<Iconify icon="basil:arrow-right-outline" sx={{ color: 'white' }} />}
            sx={{ mb: 3, bgcolor: '#3f3456', borderRadius: '15px' }}
          >
            Deposit Amount
          </Button>
        ) : (
          <Button
            variant="contained"
            endIcon={<Iconify icon="basil:arrow-right-outline" sx={{ color: 'white' }} />}
            sx={{ mb: 3, bgcolor: '#3f3456', borderRadius: '15px' }}
          >
            Deposit Amount
          </Button>
        )}
      </Stack>
      <Box>
        <Stack
          direction="row"
          alignItems="center"
          sx={{ background: 'pink', borderRadius: '10px', p: 2 }}
        >
          <Iconify icon="fluent:warning-24-regular" sx={{ color: 'red', width: '100px' }} />
          <Typography sx={{ fontSize: '12px', textAlign: 'justify', color: 'red' }}>
            Using this card, any online transaction OTP will be sent as an app notification. Please
            continue with the transaction using any text or email OTP received during the payment
            process.
          </Typography>
        </Stack>
      </Box>

      <CardServicePanel />
    </Container>
  );
}

// Main Application Wrapper
const CryptoCardApp: FC<ComponentTypeProps> = ({ block }) => {
  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (event: any, newValue: SetStateAction<number>) => {
    setCurrentTab(newValue);
  };

  return (
    <Box sx={{ bgcolor: '#F5F5F5', p: 2 }}>
      <Tabs value={currentTab} onChange={handleTabChange} centered>
        <Tab label="Info" />
        <Tab label="Card" />
        <Tab label="Owerview" />
        <Tab label="Balance" />
        <Tab label="Wallet" />
        <Tab label="Exchange" />
        <Tab label="Notifications" />
        <Tab label="Deposit" />
      </Tabs>

      {currentTab === 0 && <CardInformationScreen />}
      {currentTab === 1 && <CardDetailsView />}
      {currentTab === 2 && <CardView />}
      {currentTab === 3 && <CardOverview />}
      {currentTab === 4 && <CryptoWallet />}
      {currentTab === 5 && <Exchange />}
      {currentTab === 6 && <Notifications />}
      {currentTab === 7 && <Deposit />}

      {/* <CardDetailsView /> */}
      {/* <CardView /> */}
      {/* <CardOverview /> */}
      {/* <CryptoWallet /> */}
      {/* <CardInformationScreen /> */}
    </Box>
  );
};

export default CryptoCardApp;
